import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GeolocationService } from '@ng-web-apis/geolocation';
import { AuthenticationService, DefaultsService, ServiceBase } from '../index';
import { GeoLocationRequest, GeoLocationResponse, Client } from '../../models';
import { take } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FiltersService } from 'src/app/filters/filters.service';
import { MetricsService } from './metrics.service';

@Injectable()
export class LocationService extends MetricsService {
  geoLocationOptions = { timeout: 30000 };
  geoLocationRequest?: GeoLocationRequest;
  showDistance = true;
  coordinates?: GeolocationCoordinates;
  positionLoaded = new Subject<GeolocationPosition>();
  locationServiceErrorLoaded = new Subject<Error>();
  geoLocationResponseLoaded = new Subject<Array<GeoLocationResponse>>();
  geoLocations?: Array<GeoLocationResponse> = undefined;
  geosubscription: Subscription;
  constructor(
    public http: HttpClient,
    public geolocationService: GeolocationService,
    public translateService: TranslateService,
    protected filtersService: FiltersService,
    protected defaultsService: DefaultsService,
    protected authenticationService: AuthenticationService
  ) {
    super(
      translateService,
      filtersService,
      defaultsService,
      authenticationService
    );
  }

  clearCache = () => {
    this.geoLocations = undefined;
    this.coordinates = undefined;
  };

  getCurrentPosition = () => {
    this.geoLocationRequest = undefined;

    this.geolocationService.pipe(take(1)).subscribe(
      (position) => {
        if (this.updateCoordinates(position)) {
          this.coordinates = position.coords;
          this.positionLoaded.next(position);
        }
      },
      (err) => {
        console.error('Error getting location', err);
        this.locationServiceErrorLoaded.next(err);
      }
    );
  };

  updateCoordinates(position: GeolocationPosition) {
    if (position && position.coords) {
      if (
        this.coordinates &&
        position.coords.latitude === this.coordinates.latitude &&
        position.coords.longitude === this.coordinates.longitude
      ) {
        return false;
      }
    }

    return true;
  }

  getLocations = (clientId: number) => {
    const url = `${this.baseUrl}location-services`;
    if (this.coordinates) {
      this.geoLocationRequest = new GeoLocationRequest({
        Latitude: this.coordinates.latitude,
        Longitude: this.coordinates.longitude,
        Accuracy: this.coordinates.accuracy,
        Altitude: this.coordinates.altitude,
        AltitudeAccuracy: this.coordinates.altitudeAccuracy,
        Heading: this.coordinates.heading,
        Speed: this.coordinates.speed,
        PageSize: 10000,
      });

      const postBody = <any>this.geoLocationRequest;

      postBody.ClientID = clientId;

      this.http
        .post<Array<GeoLocationResponse>>(url, this.geoLocationRequest)
        .subscribe((geolocations) => {
          this.geoLocations = [...geolocations];
          this.geoLocationResponseLoaded.next(this.geoLocations);
        });
    }
  };

  getLocationsByZip = (zipCode: string, clientId: number) => {
    const url = `${this.baseUrl}location-services`;

    this.geoLocationRequest = new GeoLocationRequest({
      ZipCode: zipCode,
      PageSize: 10000,
    });

    const postBody = <any>this.geoLocationRequest;

    postBody.ClientID = clientId;

    this.http
      .post<Array<GeoLocationResponse>>(url, this.geoLocationRequest)
      .subscribe((geolocations) => {
        this.geoLocations = [...geolocations];
        this.geoLocationResponseLoaded.next(this.geoLocations);
      });
  };

  validatePostalCode = (zipCode: string) => {
    if (!zipCode) {
      return false;
    }

    zipCode = zipCode.trim().toUpperCase();

    const us = new RegExp(/^[0-9]{5}(?:-[0-9]{4})?$/);
    const ca = new RegExp(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/);
    const uk = new RegExp(
      /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) {0,1}[0-9][A-Za-z]{2})$/
    );
    const au = new RegExp(/\\d{4}/);
    const jp = new RegExp(/[0-9]{3}-[0-9]{4}$/);

    const isValid =
      us.test(zipCode) ||
      ca.test(zipCode) ||
      uk.test(zipCode) ||
      au.test(zipCode) ||
      jp.test(zipCode);
    return isValid;
  };

  conditionallyTrimPostalCode = (zipCode: string) => {
    let modifiedZipCode = zipCode;
    if (zipCode && zipCode.length > 3) {
      const regex = RegExp(/[a-zA-Z]/);
      if (regex.test(modifiedZipCode)) {
        modifiedZipCode = modifiedZipCode.substring(0, 3);
        return modifiedZipCode;
      }
    }

    return zipCode;
  };
}
